import { ReactNode, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  getRestaurant,
  getRestaurantWithMenuAndCart,
} from '@/actions/restaurant/get-restaurant';
import { Radar } from '@/app/1-restaurant/components/radar';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/i18n/client';
import { useNavigate } from '@/lib/navigation';
import { cn } from '@/lib/utils';
import { useMainStore } from '@/stores/main-store';

type Props = {
  restaurantId: string;
  children: ReactNode;
};

export function RestaurantLoader(props: Props) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasErrored, setHasErrored] = useState(false);

  const { setRestaurant, setMenu, setCart, setTableNumber } = useMainStore(
    (state) => ({
      setRestaurant: state.setRestaurant,
      setMenu: state.setMenu,
      setCart: state.setCart,
      setTableNumber: state.setTableNumber,
    }),
  );

  async function load() {
    setHasErrored(false);

    let data;
    try {
      data = await getRestaurantWithMenuAndCart({
        restaurantId: props.restaurantId,
      });
    } catch {
      setHasErrored(true);
      return;
    }

    setRestaurant(data.restaurant);
    setMenu(data.menu);
    setCart(data.cart);

    setHasLoaded(true);
  }

  useEffect(() => {
    const tableNumber = searchParams.get('tableNumber');
    if (tableNumber && parseInt(tableNumber)) {
      setTableNumber(tableNumber);
    }
  }, [searchParams]);

  useEffect(() => {
    load().catch();
    return () => {
      setHasLoaded(false);
    };
  }, [props.restaurantId]);

  /**
   * Poll for restaurant updates every few seconds
   */
  useEffect(() => {
    const POLL_INTERVAL = 5_000;
    const MAX_POLL_TIME = 300_000; // 5 minutes

    let timeoutId: NodeJS.Timeout;
    let nrOfPolls = 0;

    const fetchRestaurant = async () => {
      try {
        const data = await getRestaurant({
          restaurantId: props.restaurantId,
        });
        setRestaurant(data);
      } catch {
        // Ignore
      } finally {
        nrOfPolls += 1;
        if (nrOfPolls * POLL_INTERVAL < MAX_POLL_TIME) {
          timeoutId = setTimeout(fetchRestaurant, POLL_INTERVAL);
        }
      }
    };

    fetchRestaurant();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [props.restaurantId]);

  return (
    <div className="h-full w-full">
      {hasLoaded && props.children}

      <div
        className={cn(
          'absolute bottom-0 left-0 right-0 top-0 bg-white transition-opacity duration-500',
          hasLoaded
            ? 'pointer-events-none opacity-0'
            : 'pointer-events-auto opacity-100',
        )}
      >
        <div
          className={cn(
            'absolute bottom-0 left-0 right-0 flex h-1/3 w-full flex-col items-center justify-center gap-4 p-8 transition-opacity duration-200',
            hasErrored
              ? 'pointer-events-auto opacity-100'
              : 'pointer-events-none opacity-0',
          )}
        >
          <p className="text-md text-center">
            {t(
              'Home - Something went wrong',
              'Sorry, something went wrong loading the restaurant menu. Please try again.',
            )}
          </p>
          <div className="flex items-center gap-1.5 self-stretch">
            {pathname !== `/${props.restaurantId}/` && (
              <Button
                className="flex-1"
                onClick={() => navigate(`/${props.restaurantId}/`)}
              >
                {t('Home - Go Back', 'Go back')}
              </Button>
            )}
            <Button
              className="flex-1 bg-laaxred hover:bg-laaxreddark"
              onClick={load}
            >
              {t('Home - Retry', 'Retry')}
            </Button>
          </div>
        </div>

        <Radar
          style={{
            position: 'absolute',
            width: '25vw',
            height: '25vw',
            top: 'calc(33% - 25vw / 2)',
            left: 'calc(50% - 25vw / 2)',
          }}
        />
      </div>
    </div>
  );
}
